/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { KeyboardEvent, MouseEvent, PointerEvent } from "react"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { toast } from "sonner"

import type { ProjectSharingState } from "@/features/projects/utils/project-sharing-state"

import {
  CheckCircle2,
  ChevronDown,
  Circle,
  Crown,
  EllipsisVertical,
  Folder,
  FolderPlus,
  LogOut,
  Search,
  Star,
  Trash2,
  Users,
  X,
} from "@/components/icons"
import { SidebarNavSection } from "@/components/sidebar/sidebar-nav-section"
import { SidebarSearchResultItem } from "@/components/sidebar/sidebar-search-result-item"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Spinner } from "@/components/ui/spinner"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useSidebar } from "@/components/ui/use-sidebar"
import { useRoutes } from "@/contexts/use-routes"
import { ProjectUpdatesStrip } from "@/features/projects/components/project-updates-strip"
import { ProjectsDialog } from "@/features/projects/components/projects-dialog"
import { useCurrentSessionProject } from "@/features/projects/hooks/use-current-session-project"
import { useProjectFavorites } from "@/features/projects/hooks/use-project-favorites"
import {
  getProjectSharingState,
  matchesProjectShareFilter,
} from "@/features/projects/utils/project-sharing-state"
import {
  ParticipantAvatar,
  ParticipantFacepile,
} from "@/features/sharing/components/participant-avatar"
import { ShareFilterRow } from "@/features/sharing/components/share-filter-row"
import { useStoredShareFilter } from "@/features/sharing/hooks/use-stored-share-filter"
import { usePrefetchProject } from "@/hooks/use-prefetch"
import { useProjects, useRemoveParticipant } from "@/hooks/use-projects"
import { queryKeys } from "@/lib/query-keys"
import { cn } from "@/lib/utils"
import { type Project, ProjectService } from "@/services/project-service"
import { SettingsService } from "@/services/settings-service"
import { getProjectBasePath, getProjectsListPath } from "@/utils/route-helpers"

const DEFAULT_VISIBLE_COUNT = 5
const MAX_COLLAPSED_FAVORITES = 3

type ProjectsFolderProps = {
  className?: string
  defaultExpanded?: boolean
  globalSearchTerm?: string
  sessionType: string
}

export function ProjectsFolder({
  className,
  defaultExpanded = true,
  globalSearchTerm = "",
  sessionType,
}: ProjectsFolderProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { routes } = useRoutes()
  const { state: sidebarState, isMobile } = useSidebar()
  const isCollapsedSidebar = sidebarState === "collapsed" && !isMobile

  const { data: projects, isPending: isLoadingProjects } = useProjects(sessionType)
  const prefetchProject = usePrefetchProject()
  const projectBasePath = useMemo(
    () => getProjectBasePath(sessionType, routes),
    [routes, sessionType]
  )
  const projectsListPath = useMemo(
    () => getProjectsListPath(sessionType, routes),
    [routes, sessionType]
  )

  const currentUserOid = SettingsService.getInstance().claims.oid
  const { isFavorited, toggleFavoriteProject, favoriteProjectIds, canToggleFavorites } =
    useProjectFavorites(sessionType)
  const removeParticipant = useRemoveParticipant()

  const [open, setOpen] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null)
  const [showLeaveDialog, setShowLeaveDialog] = useState(false)
  const [projectToLeave, setProjectToLeave] = useState<Project | null>(null)
  const [localSearchTerm, setLocalSearchTerm] = useState("")
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [selectMode, setSelectMode] = useState(false)
  const [selectedProjects, setSelectedProjects] = useState<Project[]>([])
  const [showDeleteProjectsDialog, setShowDeleteProjectsDialog] = useState(false)
  const [showAllItems, setShowAllItems] = useState(false)
  const [isCollapsedPopoverOpen, setIsCollapsedPopoverOpen] = useState(false)
  const [showAllInCollapsed, setShowAllInCollapsed] = useState(false)
  const [openMenuProjectId, setOpenMenuProjectId] = useState<string | null>(null)
  const location = useLocation()
  const currentSessionProjectId = useCurrentSessionProject()
  const longPressTimerRef = useRef<number | null>(null)
  const longPressTriggeredRef = useRef(false)
  const startPositionRef = useRef<{ x: number; y: number } | null>(null)
  const longPressThresholdMs = 500
  const maxMovementPx = 10

  const effectiveSearchTerm = globalSearchTerm || localSearchTerm
  const isSearchingProjects = Boolean(effectiveSearchTerm)
  const favoritesSectionStorageKey = `sidebar-projects-favorites-open-${sessionType}`

  const [isFavoritesSectionOpenStored, setIsFavoritesSectionOpenStored] = useState(() => {
    if (typeof window === "undefined") return true
    const stored = localStorage.getItem(favoritesSectionStorageKey)
    return stored !== null ? stored === "true" : true
  })

  // Persisted "All | Shared with me | Shared by me" filter (remembers last used per session type)
  const [shareFilter, setShareFilter] = useStoredShareFilter(
    `sidebar-projects-share-filter-${sessionType}`
  )

  useEffect(() => {
    return () => {
      if (longPressTimerRef.current !== null) {
        window.clearTimeout(longPressTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (typeof window === "undefined") {
      setIsFavoritesSectionOpenStored(true)
      return
    }
    const stored = localStorage.getItem(favoritesSectionStorageKey)
    setIsFavoritesSectionOpenStored(stored !== null ? stored === "true" : true)
  }, [favoritesSectionStorageKey])

  const setIsFavoritesSectionOpen = useCallback(
    (open: boolean) => {
      setIsFavoritesSectionOpenStored(open)
      if (typeof window !== "undefined") {
        localStorage.setItem(favoritesSectionStorageKey, String(open))
      }
    },
    [favoritesSectionStorageKey]
  )

  const isFavoritesSectionOpen = isSearchingProjects ? true : isFavoritesSectionOpenStored

  const deleteProject = useMutation({
    mutationFn: (projectId: string) => ProjectService.deleteProject(projectId),
    onSuccess: (_data, deletedProjectId) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects(sessionType) })
      if (location.pathname.includes(deletedProjectId)) {
        void navigate(projectsListPath)
      }
      if (projectToDelete) {
        setShowDeleteDialog(false)
        setProjectToDelete(null)
      }
    },
    onError: (error: Error) => {
      toast.error("Failed to delete project", {
        description: error.message || "An error occurred",
      })
      setShowDeleteDialog(false)
      setProjectToDelete(null)
    },
  })

  const handleLeave = (project: Project) => {
    setProjectToLeave(project)
    setShowLeaveDialog(true)
  }

  const confirmLeave = async () => {
    if (!projectToLeave || !currentUserOid) return
    const projectId = projectToLeave.id
    try {
      await removeParticipant.mutateAsync({
        projectId,
        participantOid: currentUserOid,
      })
      setShowLeaveDialog(false)
      setProjectToLeave(null)
      if (location.pathname.includes(projectId)) {
        void navigate(projectsListPath)
      }
    } catch {
      // onError toast is handled in the mutation hook; keep dialog open on failure
    }
  }

  const handleDelete = (project: Project) => {
    setProjectToDelete(project)
    setShowDeleteDialog(true)
  }

  const confirmDelete = () => {
    if (projectToDelete) {
      deleteProject.mutate(projectToDelete.id)
    }
  }

  // Pure and cheap over sidebar-sized lists — computed directly per call,
  // exactly like the Chats section does with getChatSharingState.
  const getSharingState = useCallback(
    (project: Project): ProjectSharingState => getProjectSharingState(project, currentUserOid),
    [currentUserOid]
  )

  const passesShareFilter = useCallback(
    (project: Project) => matchesProjectShareFilter(getSharingState(project), shareFilter),
    [getSharingState, shareFilter]
  )

  const filteredProjects = useMemo(() => {
    const base = (projects ?? []).filter(passesShareFilter)
    if (!effectiveSearchTerm) {
      return base
    }
    return base.filter(
      (proj) =>
        proj.name?.toLowerCase().includes(effectiveSearchTerm.toLowerCase()) ||
        proj.id.toLowerCase().includes(effectiveSearchTerm.toLowerCase())
    )
  }, [projects, passesShareFilter, effectiveSearchTerm])

  const favoriteIdsSet = useMemo(() => new Set(favoriteProjectIds), [favoriteProjectIds])

  // Favourites obey the active share filter, exactly like the Chats section.
  const favoriteProjects = useMemo(
    () => filteredProjects.filter((proj) => favoriteIdsSet.has(proj.id)),
    [filteredProjects, favoriteIdsSet]
  )

  const recentProjects = useMemo(
    () => filteredProjects.filter((proj) => !favoriteIdsSet.has(proj.id)),
    [filteredProjects, favoriteIdsSet]
  )

  const { visibleFavoriteProjects, visibleRecentProjects } = useMemo(() => {
    if (showAllItems || effectiveSearchTerm) {
      return { visibleFavoriteProjects: favoriteProjects, visibleRecentProjects: recentProjects }
    }
    const favorites = favoriteProjects.slice(0, DEFAULT_VISIBLE_COUNT)
    const rest = recentProjects.slice(0, Math.max(0, DEFAULT_VISIBLE_COUNT - favorites.length))
    return { visibleFavoriteProjects: favorites, visibleRecentProjects: rest }
  }, [favoriteProjects, recentProjects, showAllItems, effectiveSearchTerm])

  const deletableProjects = useMemo(() => {
    return filteredProjects.filter((proj) => proj.currentUserRole === "owner")
  }, [filteredProjects])

  const canDeleteProject = (project: Project) => project.currentUserRole === "owner"

  const handleSelectModeToggle = (project?: Project) => {
    setSelectMode(true)
    setSelectedProjects(project ? [project] : [])
  }

  const handleExitSelectMode = () => {
    setSelectMode(false)
    setSelectedProjects([])
  }

  const handleDeleteSelected = () => {
    setShowDeleteProjectsDialog(true)
  }

  const confirmDeleteSelected = () => {
    selectedProjects.forEach((project) => {
      deleteProject.mutate(project.id)
    })
    setShowDeleteProjectsDialog(false)
    setSelectedProjects([])
    setSelectMode(false)
  }

  const clearLongPressTimer = () => {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
  }

  const handleItemPointerDown = (
    event: PointerEvent<HTMLElement>,
    project: Project,
    isActionAllowed: boolean
  ) => {
    if (event.pointerType !== "touch") return
    if (selectMode || !isActionAllowed) return
    longPressTriggeredRef.current = false
    startPositionRef.current = { x: event.clientX, y: event.clientY }
    clearLongPressTimer()
    longPressTimerRef.current = window.setTimeout(() => {
      longPressTriggeredRef.current = true
      handleSelectModeToggle(project)
    }, longPressThresholdMs)
  }

  const handleItemPointerMove = (event: PointerEvent<HTMLElement>) => {
    if (event.pointerType !== "touch") return
    if (!startPositionRef.current) return
    const dx = Math.abs(event.clientX - startPositionRef.current.x)
    const dy = Math.abs(event.clientY - startPositionRef.current.y)
    if (Math.max(dx, dy) > maxMovementPx) {
      clearLongPressTimer()
      startPositionRef.current = null
    }
  }

  const handleItemPointerUp = (event: PointerEvent<HTMLElement>) => {
    if (event.pointerType !== "touch") return
    clearLongPressTimer()
    startPositionRef.current = null
    if (longPressTriggeredRef.current) {
      event.preventDefault()
      event.stopPropagation()
      // The synthetic click (when the browser emits one) is dispatched in the
      // same input sequence as this pointerup; drop the flag right after so it
      // only ever suppresses that click — not a later, unrelated tap.
      window.setTimeout(() => {
        longPressTriggeredRef.current = false
      }, 0)
    }
  }

  const handleItemPointerCancel = (event: PointerEvent<HTMLElement>) => {
    if (event.pointerType !== "touch") return
    clearLongPressTimer()
    startPositionRef.current = null
  }

  const handleItemContextMenu = (event: MouseEvent<HTMLElement>) => {
    if (longPressTriggeredRef.current) {
      event.preventDefault()
    }
  }

  const toggleProjectSelection = (project: Project) => {
    // Swallow the click that follows a completed long-press: the row has
    // already been selected by entering select mode, and toggling here would
    // immediately deselect it.
    if (longPressTriggeredRef.current) {
      longPressTriggeredRef.current = false
      return
    }
    if (!canDeleteProject(project)) return

    setSelectedProjects((prev) => {
      const isSelected = prev.some((p) => p.id === project.id)
      if (isSelected) {
        return prev.filter((p) => p.id !== project.id)
      }
      return [...prev, project]
    })
  }

  const handleCollapsedProjectClick = useCallback(
    (project: Project) => {
      void navigate(`${projectBasePath}/${project.id}`)
      setIsCollapsedPopoverOpen(false)
    },
    [navigate, projectBasePath]
  )

  const handleOpenUpdate = useCallback(
    (project: Project) => {
      void navigate(`${projectBasePath}/${project.id}`)
    },
    [navigate, projectBasePath]
  )

  const { collapsedFavoriteProjects, collapsedRecentProjects } = useMemo(() => {
    if (effectiveSearchTerm || showAllInCollapsed) {
      return {
        collapsedFavoriteProjects: favoriteProjects,
        collapsedRecentProjects: recentProjects,
      }
    }
    const favorites = favoriteProjects.slice(0, MAX_COLLAPSED_FAVORITES)
    const rest = recentProjects.slice(0, Math.max(0, DEFAULT_VISIBLE_COUNT - favorites.length))
    return { collapsedFavoriteProjects: favorites, collapsedRecentProjects: rest }
  }, [favoriteProjects, recentProjects, effectiveSearchTerm, showAllInCollapsed])

  const hasMoreProjects =
    filteredProjects.length > collapsedFavoriteProjects.length + collapsedRecentProjects.length

  const getRoleLabel = (project: Project) => {
    const role = project.currentUserRole
    if (role === "reader") return t("sharing.role_reader", { defaultValue: "Reader" })
    if (role === "editor") return t("sharing.role_editor", { defaultValue: "Editor" })
    if (role === "admin") return t("sharing.role_admin", { defaultValue: "Admin" })
    if (role === "owner") return t("sharing.role_owner", { defaultValue: "Owner" })
    return undefined
  }

  const isProjectActive = (project: Project) => {
    // Project pages are /projects/:id (standalone or module-nested); chats
    // inside a project live on the module chat route and are covered by the
    // currentSessionProjectId check below.
    const isOnProjectPage = location.pathname.match(/\/projects\/([^/]+)$/)
    if (isOnProjectPage) return isOnProjectPage[1] === project.id
    if (currentSessionProjectId) return currentSessionProjectId === project.id
    return false
  }

  const canLeaveProject = (project: Project) =>
    project.currentUserRole !== "owner" &&
    project.currentUserRole !== undefined &&
    (project.participants ?? []).some((p) => p.oid === currentUserOid && p.type === "user")

  // Leading glyph (left of the title): the folder icon by default, or a crown
  // for ownership transfer. Always visible (identity), unlike the trailing
  // marker which yields to the hover menu. Mirrors the chat row anatomy.
  const renderLeadingGlyph = (state: ProjectSharingState, isActive: boolean) => {
    const wrap = "flex size-[18px] shrink-0 items-center justify-center"
    if (state.isOwnerTransfer) {
      return (
        <span
          className={cn(wrap, "text-warning")}
          title={t("projects.made_owner_tooltip", { defaultValue: "You were made owner" })}
        >
          <Crown className="size-4" />
        </span>
      )
    }
    return (
      <span
        className={cn(wrap, isActive ? "text-sidebar-accent-foreground" : "text-muted-foreground")}
      >
        <Folder className="size-4" />
      </span>
    )
  }

  // Trailing marker (right of the title): sharer avatar for incoming shares,
  // recipient facepile for outgoing, or an "Owner" tag for ownership transfer.
  // Fades out on hover so the dropdown-menu trigger can take its place.
  const renderTrailingMarker = (
    proj: Project,
    state: ProjectSharingState,
    canShowActions: boolean
  ) => {
    const markerClass = cn(
      "shrink-0 transition-opacity duration-150",
      canShowActions && "group-hover/item:opacity-0",
      openMenuProjectId === proj.id && "opacity-0"
    )
    if (state.isOwnerTransfer) {
      return (
        <span
          className={cn(
            markerClass,
            "bg-warning/15 text-warning-foreground rounded px-1.5 py-0.5 text-[10px] font-medium"
          )}
        >
          {t("sharing.role_owner", { defaultValue: "Owner" })}
        </span>
      )
    }
    if (state.relationship === "shared_with_me") {
      return state.sharer ? (
        <ParticipantAvatar className={markerClass} participant={state.sharer} size={18} />
      ) : (
        <span className={markerClass}>
          <Users className="text-muted-foreground size-3.5" />
        </span>
      )
    }
    if (state.relationship === "shared_by_me" && state.recipients.length > 0) {
      return (
        <ParticipantFacepile
          className={markerClass}
          max={3}
          participants={state.recipients}
          size={18}
        />
      )
    }
    return null
  }

  const renderProjectItem = (proj: Project, itemIndex?: number) => {
    const canManageProject = canDeleteProject(proj)
    const canLeave = canLeaveProject(proj)
    const isProjectFavorited = isFavorited(proj.id)
    const canShowProjectActions = canManageProject || canLeave || canToggleFavorites
    const sharingState = getSharingState(proj)
    const isActive = isProjectActive(proj)

    const isSelectedInSelectMode = selectedProjects.some((p) => p.id === proj.id)

    if (selectMode && !canManageProject) {
      // Only owners can be bulk-selected, so a non-owner row is not a toggle: it stays
      // out of the tab order and carries no pressed state it could never change.
      return (
        <div
          key={proj.id}
          aria-disabled="true"
          data-testid="project-item"
          className={cn(
            "group/nav-item relative flex w-full items-center gap-2 rounded-md px-2 py-1.5 transition-colors duration-150",
            isActive && "bg-sidebar-accent text-sidebar-accent-foreground"
          )}
          onContextMenu={handleItemContextMenu}
        >
          {renderLeadingGlyph(sharingState, isActive)}
          <span className="flex-1 truncate text-sm">{proj.name || proj.id}</span>
        </div>
      )
    }

    if (selectMode) {
      return (
        <button
          key={proj.id}
          aria-pressed={isSelectedInSelectMode}
          data-testid="project-item"
          type="button"
          className={cn(
            "group/nav-item relative flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors duration-150",
            "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset",
            "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
            "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
            isActive && "bg-sidebar-accent text-sidebar-accent-foreground"
          )}
          onClick={() => toggleProjectSelection(proj)}
          onContextMenu={handleItemContextMenu}
          onPointerCancel={handleItemPointerCancel}
          onPointerDown={(event) => handleItemPointerDown(event, proj, canManageProject)}
          onPointerMove={handleItemPointerMove}
          onPointerUp={handleItemPointerUp}
        >
          {renderLeadingGlyph(sharingState, isActive)}
          <span className="flex-1 truncate text-sm">{proj.name || proj.id}</span>
          <span className="absolute top-1/2 right-1 -translate-y-1/2 rounded-md p-1">
            {isSelectedInSelectMode ? (
              <CheckCircle2 className="text-primary size-5" />
            ) : (
              <Circle className="text-muted-foreground size-5" />
            )}
          </span>
        </button>
      )
    }

    const projectPath = `${projectBasePath}/${proj.id}`

    const handleNavigateToProject = () => {
      // A completed long-press entered select mode; swallow the click that
      // follows on touch devices instead of navigating away.
      if (longPressTriggeredRef.current) {
        longPressTriggeredRef.current = false
        return
      }
      void navigate(projectPath)
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault()
        handleNavigateToProject()
      }
    }

    const item = (
      <Tooltip key={proj.id}>
        <div
          data-testid="project-item"
          role="link"
          tabIndex={0}
          className={cn(
            "group/item relative isolate flex w-full cursor-pointer items-center rounded-md",
            "hover:text-sidebar-accent-foreground",
            isActive && "bg-sidebar-accent text-sidebar-accent-foreground"
          )}
          onClick={handleNavigateToProject}
          onContextMenu={handleItemContextMenu}
          onKeyDown={handleKeyDown}
          onMouseEnter={() => prefetchProject(proj.id, sessionType)}
          onPointerCancel={handleItemPointerCancel}
          onPointerDown={(event) => handleItemPointerDown(event, proj, canManageProject)}
          onPointerMove={handleItemPointerMove}
          onPointerUp={handleItemPointerUp}
        >
          {!isActive && (
            <div
              aria-hidden="true"
              className="bg-sidebar-accent pointer-events-none absolute inset-0 -z-10 rounded-md opacity-0 transition-opacity duration-150 group-hover/item:opacity-100"
            />
          )}
          <TooltipTrigger className="flex w-full items-center justify-start gap-2 px-2 py-1.5">
            {renderLeadingGlyph(sharingState, isActive)}
            <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
              <div className="flex min-w-0 flex-1 items-center gap-1.5">
                <span className="flex-1 truncate text-left text-sm">{proj.name || proj.id}</span>
                {proj.hasUnviewedNotification && (
                  <span className="bg-primary size-2 shrink-0 rounded-full" />
                )}
              </div>
              {renderTrailingMarker(proj, sharingState, canShowProjectActions)}
            </div>
          </TooltipTrigger>
          {canShowProjectActions && (
            <DropdownMenu
              open={openMenuProjectId === proj.id}
              onOpenChange={(isOpen) => setOpenMenuProjectId(isOpen ? proj.id : null)}
            >
              <DropdownMenuTrigger
                data-testid={`project-item-dropdown-menu-trigger-${proj.id}`}
                className={cn(
                  "absolute top-1/2 right-1 -translate-y-1/2 rounded-md p-1 transition-opacity duration-150",
                  "opacity-0 group-hover/item:opacity-100",
                  "hover:bg-sidebar-accent-foreground/15",
                  openMenuProjectId === proj.id && "opacity-100"
                )}
                onClick={(event) => event.stopPropagation()}
              >
                <EllipsisVertical className="size-3.5 shrink-0" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="min-w-[180px]" side="right">
                <DropdownMenuItem
                  className="gap-2"
                  onClick={(event) => {
                    event.stopPropagation()
                    toggleFavoriteProject(proj.id)
                  }}
                >
                  <Star className="size-4" />
                  {isProjectFavorited
                    ? t("projects.remove_favorite", { defaultValue: "Remove favorite" })
                    : t("projects.mark_as_favorite", { defaultValue: "Mark as favorite" })}
                </DropdownMenuItem>
                {canManageProject && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="gap-2"
                      onClick={(event) => {
                        event.stopPropagation()
                        handleSelectModeToggle(proj)
                      }}
                    >
                      <CheckCircle2 className="size-4" />
                      {t("chatsession.select_mode", { defaultValue: "Select" })}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive gap-2"
                      onClick={(event) => {
                        event.stopPropagation()
                        handleDelete(proj)
                      }}
                    >
                      <Trash2 className="size-4" />
                      {t("projects.delete")}
                    </DropdownMenuItem>
                  </>
                )}
                {canLeave && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive gap-2"
                      onClick={(event) => {
                        event.stopPropagation()
                        handleLeave(proj)
                      }}
                    >
                      <LogOut className="size-4" />
                      {t("projects.leave_project", { defaultValue: "Leave project" })}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        <TooltipContent className="max-w-[280px] p-3" side="right" sideOffset={8}>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Folder className="size-4" />
              <p className="text-sm font-medium">{proj.name || proj.id}</p>
            </div>
            {getRoleLabel(proj) &&
              (proj.currentUserRole !== "owner" || (proj.participants?.length ?? 0) > 1) && (
                <p className="text-muted-foreground text-xs">{getRoleLabel(proj)}</p>
              )}
          </div>
        </TooltipContent>
      </Tooltip>
    )

    if (itemIndex !== undefined && itemIndex < 10) {
      return (
        <div
          key={proj.id}
          className="sidebar-stagger-item"
          style={{ "--stagger-index": itemIndex } as React.CSSProperties}
        >
          {item}
        </div>
      )
    }
    return item
  }

  const headerActions = (
    <>
      {!globalSearchTerm && (
        <Button
          aria-label={t("projects.search", { defaultValue: "Search" })}
          size="icon"
          variant="ghost"
          className={cn(
            "hover:bg-foreground/15 size-7 rounded-full p-0",
            localSearchTerm && "bg-sidebar-accent text-sidebar-accent-foreground"
          )}
          onClick={(event) => {
            event.stopPropagation()
            setIsSearchOpen(true)
          }}
        >
          <Search className="size-4" />
        </Button>
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            aria-label={t("projects.new_project")}
            className="hover:bg-foreground/15 size-7 rounded-full p-0"
            data-testid="new-project-button"
            size="icon"
            variant="ghost"
            onClick={(event) => {
              event.stopPropagation()
              setOpen(true)
            }}
          >
            <FolderPlus className="size-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" sideOffset={5}>
          <p>{t("projects.new_project")}</p>
        </TooltipContent>
      </Tooltip>
    </>
  )

  const getEmptyStateMessage = () => {
    // No projects at all trumps the filter-scoped message — an active share
    // filter over an empty list is not "filtered out", there is just nothing.
    if ((projects ?? []).length === 0) {
      return t("projects.no_projects", { defaultValue: "No projects yet" })
    }
    if (effectiveSearchTerm) return t("projects.no_matches", { defaultValue: "No projects found" })
    if (shareFilter !== "all") {
      return t("projects.no_shared_projects", { defaultValue: "Nothing here yet" })
    }
    return t("projects.no_projects", { defaultValue: "No projects yet" })
  }

  if (isCollapsedSidebar) {
    const hasNoResults =
      !isLoadingProjects &&
      (projects ?? []).length > 0 &&
      collapsedFavoriteProjects.length === 0 &&
      collapsedRecentProjects.length === 0

    return (
      <>
        <Popover
          open={isCollapsedPopoverOpen}
          onOpenChange={(open) => {
            setIsCollapsedPopoverOpen(open)
            if (!open) setShowAllInCollapsed(false)
          }}
        >
          <PopoverTrigger asChild>
            <button
              aria-label={t("projects.title")}
              type="button"
              className={cn(
                "group/nav-section hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-ring flex size-10 items-center justify-center rounded-md transition-colors duration-150 focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset",
                className
              )}
            >
              <Folder className="size-6" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="flex w-72 flex-col p-0"
            side="right"
            sideOffset={8}
          >
            <div className="border-b p-2">
              <div className="relative">
                <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
                <Input
                  aria-label={t("projects.search_placeholder")}
                  className="placeholder:text-muted-foreground h-8 pl-9 text-sm"
                  placeholder={t("projects.search_placeholder")}
                  value={localSearchTerm}
                  onChange={(e) => setLocalSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === "Escape" && localSearchTerm && setLocalSearchTerm("")}
                />
              </div>
            </div>
            <div className="border-b px-1 py-1">
              <ShareFilterRow
                testIdPrefix="projects-share-filter"
                value={shareFilter}
                onChange={setShareFilter}
              />
            </div>
            <div className="relative flex min-h-0 flex-1 flex-col">
              {showAllInCollapsed && (
                <div className="from-popover pointer-events-none absolute top-0 right-0 left-0 z-10 h-4 bg-gradient-to-b to-transparent" />
              )}
              <div
                className={cn(
                  "overflow-y-auto",
                  showAllInCollapsed ? "max-h-[300px] pb-10" : "max-h-[300px]"
                )}
              >
                <div className="flex flex-col gap-2 p-1">
                  {isLoadingProjects && (
                    <p className="text-muted-foreground px-2 py-4 text-center text-xs">
                      {t("projects.loading_projects", { defaultValue: "Loading projects..." })}
                    </p>
                  )}
                  {!isLoadingProjects && (projects ?? []).length === 0 && (
                    <p className="text-muted-foreground px-2 py-4 text-center text-xs">
                      {t("projects.no_projects", { defaultValue: "No projects yet" })}
                    </p>
                  )}
                  {hasNoResults && (
                    <p className="text-muted-foreground px-2 py-4 text-center text-xs">
                      {getEmptyStateMessage()}
                    </p>
                  )}
                  {collapsedFavoriteProjects.length > 0 && (
                    <Collapsible
                      open={isFavoritesSectionOpen}
                      onOpenChange={setIsFavoritesSectionOpen}
                    >
                      <section className="flex flex-col gap-1">
                        <CollapsibleTrigger
                          className="text-muted-foreground/70 hover:text-muted-foreground group/section flex w-full items-center gap-1 px-2 pt-1 text-[10px] font-semibold tracking-wider uppercase disabled:cursor-default disabled:opacity-70"
                          disabled={isSearchingProjects}
                          type="button"
                        >
                          {t("projects.sections.favorites", { defaultValue: "Favorites" })}
                          <ChevronDown
                            className={cn(
                              "text-muted-foreground/70 group-hover/section:text-muted-foreground size-3.5 shrink-0 -translate-y-[2px] opacity-0 transition-transform duration-150 group-hover/section:opacity-100",
                              !isFavoritesSectionOpen && "-rotate-90"
                            )}
                          />
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          {collapsedFavoriteProjects.map((project) => (
                            <SidebarSearchResultItem
                              key={project.id}
                              icon={<Folder className="size-4" />}
                              isActive={isProjectActive(project)}
                              subtitle={getRoleLabel(project)}
                              title={project.name || project.id}
                              onClick={() => handleCollapsedProjectClick(project)}
                            />
                          ))}
                        </CollapsibleContent>
                      </section>
                    </Collapsible>
                  )}
                  {collapsedRecentProjects.length > 0 && (
                    <section className="flex flex-col gap-1">
                      {collapsedRecentProjects.map((project) => (
                        <SidebarSearchResultItem
                          key={project.id}
                          icon={<Folder className="size-4" />}
                          isActive={isProjectActive(project)}
                          subtitle={getRoleLabel(project)}
                          title={project.name || project.id}
                          onClick={() => handleCollapsedProjectClick(project)}
                        />
                      ))}
                    </section>
                  )}
                  {hasMoreProjects && !effectiveSearchTerm && !showAllInCollapsed && (
                    <Button
                      className="text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground mt-1 h-7 w-full justify-start px-3 text-xs"
                      variant="ghost"
                      onClick={() => setShowAllInCollapsed(true)}
                    >
                      {`${t("sidebar.see_all", { defaultValue: "See all" })} (${filteredProjects.length})`}
                    </Button>
                  )}
                </div>
              </div>
              {showAllInCollapsed && !effectiveSearchTerm && (
                <div className="pointer-events-none absolute right-0 bottom-0 left-0 z-10 flex flex-col">
                  <div className="from-popover h-6 bg-gradient-to-t to-transparent" />
                  <div className="bg-popover px-1 pb-1">
                    <Button
                      className="text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground pointer-events-auto h-7 w-full justify-start px-3 text-xs"
                      variant="ghost"
                      onClick={() => setShowAllInCollapsed(false)}
                    >
                      {t("sidebar.show_less", { defaultValue: "Show less" })}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
        <ProjectsDialog open={open} sessionType={sessionType} onOpenChange={setOpen} />
      </>
    )
  }

  return (
    <>
      <SidebarNavSection
        allItemsShown={showAllItems}
        className={className}
        defaultExpanded={defaultExpanded}
        forceExpanded={!!globalSearchTerm}
        headerActions={headerActions}
        icon={<Folder className="size-4" />}
        isSearchActive={isSearchOpen && !globalSearchTerm}
        searchPlaceholder={t("projects.search_placeholder", { defaultValue: "Search projects..." })}
        searchTerm={localSearchTerm}
        storageKey={`sidebar-projects-collapsed-${sessionType}`}
        title={t("projects.title")}
        toggleTestId="projects-section-toggle"
        totalCount={filteredProjects.length}
        visibleCount={DEFAULT_VISIBLE_COUNT}
        onSearchChange={setLocalSearchTerm}
        onSearchClose={() => setIsSearchOpen(false)}
        onSeeAllClick={() => setShowAllItems(true)}
        onShowLessClick={() => setShowAllItems(false)}
      >
        <ProjectUpdatesStrip projects={projects ?? []} onOpen={handleOpenUpdate} />

        <ShareFilterRow
          testIdPrefix="projects-share-filter"
          value={shareFilter}
          onChange={setShareFilter}
        />

        {selectMode && (
          <div className="mb-2 flex items-center gap-1.5 px-2 text-xs">
            <span className="text-muted-foreground min-w-0 shrink-0 font-medium tabular-nums">
              {selectedProjects.length}
            </span>
            <Button
              className="h-6 min-w-0 shrink px-2 text-xs"
              disabled={deletableProjects.length === 0}
              size="sm"
              variant="outline"
              onClick={() => {
                if (selectedProjects.length === deletableProjects.length) {
                  setSelectedProjects([])
                } else {
                  setSelectedProjects(deletableProjects)
                }
              }}
            >
              <span className="truncate">
                {selectedProjects.length === deletableProjects.length &&
                deletableProjects.length > 0
                  ? t("projects.deselect_all", { defaultValue: "Deselect all" })
                  : t("chatsession.choose_all", { defaultValue: "Select all" })}
              </span>
            </Button>
            {selectedProjects.length > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    aria-label={t("projects.delete_selected", { defaultValue: "Delete selected" })}
                    className="text-destructive size-6 shrink-0 p-0"
                    size="icon"
                    variant="ghost"
                    onClick={handleDeleteSelected}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {t("projects.delete_selected", { defaultValue: "Delete selected" })}
                </TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  aria-label={t("common.cancel", { defaultValue: "Cancel" })}
                  className="size-6 shrink-0 p-0"
                  size="icon"
                  variant="ghost"
                  onClick={handleExitSelectMode}
                >
                  <X className="size-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {t("common.cancel", { defaultValue: "Cancel" })}
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        {isLoadingProjects && (
          <p className="text-muted-foreground px-2 py-4 text-center text-xs">
            {t("projects.loading_projects", { defaultValue: "Loading projects..." })}
          </p>
        )}
        {!isLoadingProjects && filteredProjects.length === 0 && (
          <p className="text-muted-foreground px-2 py-4 text-center text-xs">
            {getEmptyStateMessage()}
          </p>
        )}

        {(visibleFavoriteProjects.length > 0 || visibleRecentProjects.length > 0) && (
          <div className="flex flex-col gap-4">
            {visibleFavoriteProjects.length > 0 && (
              <Collapsible open={isFavoritesSectionOpen} onOpenChange={setIsFavoritesSectionOpen}>
                <section className="flex flex-col gap-1">
                  <CollapsibleTrigger
                    className="text-muted-foreground/70 hover:text-muted-foreground group/section flex w-full items-center gap-1 px-2 text-[10px] font-semibold tracking-wider uppercase disabled:cursor-default disabled:opacity-70"
                    disabled={isSearchingProjects}
                    type="button"
                  >
                    {t("projects.sections.favorites", { defaultValue: "Favorites" })}
                    <ChevronDown
                      className={cn(
                        "text-muted-foreground/70 group-hover/section:text-muted-foreground size-3.5 shrink-0 -translate-y-[2px] opacity-0 transition-transform duration-150 group-hover/section:opacity-100",
                        !isFavoritesSectionOpen && "-rotate-90"
                      )}
                    />
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="flex flex-col">
                      {visibleFavoriteProjects.map((proj) => renderProjectItem(proj))}
                    </div>
                  </CollapsibleContent>
                </section>
              </Collapsible>
            )}
            {visibleRecentProjects.length > 0 && (
              <section className="flex flex-col">
                {visibleRecentProjects.map((proj, index) =>
                  renderProjectItem(proj, visibleFavoriteProjects.length + index)
                )}
              </section>
            )}
          </div>
        )}
      </SidebarNavSection>

      <ProjectsDialog open={open} sessionType={sessionType} onOpenChange={setOpen} />

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("projects.confirm_delete")}</DialogTitle>
            <DialogDescription>
              {t("projects.confirm_delete_description", {
                name: projectToDelete?.name || projectToDelete?.id,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              disabled={deleteProject.isPending}
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              disabled={deleteProject.isPending}
              variant="destructive"
              onClick={confirmDelete}
            >
              {t("projects.delete")}
              {deleteProject.isPending && <Spinner className="ml-2 size-4" />}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteProjectsDialog} onOpenChange={setShowDeleteProjectsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("projects.confirm_delete_many", {
                count: selectedProjects.length,
                defaultValue: "Delete {{count}} projects?",
              })}
            </DialogTitle>
            <DialogDescription>
              {t("projects.confirm_delete_many_description", {
                defaultValue: "This action cannot be undone.",
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              disabled={deleteProject.isPending}
              variant="outline"
              onClick={() => setShowDeleteProjectsDialog(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              disabled={deleteProject.isPending}
              variant="destructive"
              onClick={confirmDeleteSelected}
            >
              {t("projects.delete")}
              {deleteProject.isPending && <Spinner className="ml-2 size-4" />}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("projects.confirm_leave", { defaultValue: "Leave project?" })}
            </DialogTitle>
            <DialogDescription>
              {t("projects.confirm_leave_description", {
                name: projectToLeave?.name || projectToLeave?.id,
                defaultValue:
                  "You will lose access to {{name}}. You will need to be invited again to regain access.",
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              disabled={removeParticipant.isPending}
              variant="outline"
              onClick={() => setShowLeaveDialog(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              disabled={removeParticipant.isPending}
              variant="destructive"
              onClick={() => void confirmLeave()}
            >
              {t("projects.leave_project", { defaultValue: "Leave project" })}
              {removeParticipant.isPending && <Spinner className="ml-2 size-4" />}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
